import React from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';
import * as zod from 'zod';
import {
  Box,
  Button,
  FormSection,
  Input,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
  TimeField,
} from '../ui';
import type { SpotForm_ExtraFreightFragment } from '../../graphql';
import { FreightType } from '../../graphql';
import { mapKeyBy } from '../../lib/util';
import { extraFreightSchema } from './schema';
import { valueOrNull } from '../../lib/zod-util';

const extraFreightTypeOptions = [
  {
    value: FreightType.LUMPSUM,
    label: 'Lumpsum',
  },
  {
    value: FreightType.RATE,
    label: 'Rate per unit',
  },
  {
    value: FreightType.PERCENTAGE,
    label: 'Percentage of freight',
  },
];

export const extraFreightFieldsSchema = zod.object({
  extraFreights: zod.array(extraFreightSchema),
});
type ExtraFreightFieldsSchema = zod.infer<typeof extraFreightFieldsSchema>;

export default function ExtraFreightFields<T extends ExtraFreightFieldsSchema>({
  extraFreights,
  form,
}: {
  extraFreights: SpotForm_ExtraFreightFragment[];
  form: UseFormReturn<T>;
}): JSX.Element {
  const extraFreightsById = mapKeyBy(extraFreights, (ef) => ef.id);

  const {
    control,
    register,
    formState: { errors },
  } = form as UseFormReturn<ExtraFreightFieldsSchema>;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'extraFreights',
    keyName: 'key',
  });

  return (
    <FormSection
      title="Extra freights"
      display="block"
      visibilityId="extra_freights"
    >
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="neutral.gray_225"
        overflow="auto"
        mb={3}
      >
        <Table>
          <thead>
            <TableHeaders>
              <TableHeader width={250}>External ID</TableHeader>
              <TableHeader width={250}>Name</TableHeader>
              <TableHeader width={250}>Amount</TableHeader>
              <TableHeader width={250}>Type</TableHeader>
              <TableHeader width={250}>Description</TableHeader>
              <TableHeader width={350}>Last external update</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableHeaders>
          </thead>
          <tbody>
            {fields.map((f, i) => {
              const ef = f.id ? extraFreightsById.get(f.id) : null;

              return (
                <TableRow key={f.key}>
                  <TableCell>{ef?.externalId}</TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      {...register(`extraFreights.${i}.name`)}
                      required
                      borderColor={
                        !!errors.extraFreights?.[i]?.name
                          ? 'status.error'
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      required
                      {...register(`extraFreights.${i}.amount`)}
                      borderColor={
                        !!errors.extraFreights?.[i]?.amount
                          ? 'status.error'
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      {...register(`extraFreights.${i}.type`, {
                        setValueAs: valueOrNull,
                      })}
                      options={extraFreightTypeOptions}
                      placeholder="-"
                      enablePlaceholder
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      {...register(`extraFreights.${i}.description`)}
                    />
                  </TableCell>
                  <TableCell>
                    {ef?.externalLastUpdate && (
                      <TimeField value={ef.externalLastUpdate} />
                    )}{' '}
                    {ef?.externalUpdatedBy && (
                      <Text>({ef.externalUpdatedBy})</Text>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="secondary" onPress={() => remove(i)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </Box>
      <Button
        variant="secondary"
        onPress={() =>
          append({
            amount: '',
            description: null,
            id: null,
            name: '',
            type: null,
            ignoreInPnl: false, // default as false, 'true' value is only from IMOS integration
          })
        }
      >
        Add extra freight item
      </Button>
    </FormSection>
  );
}
